import { useState } from 'react'
import { Alert, App, Input, Modal, Typography } from 'antd'
import { useTranslation } from 'react-i18next'
import { queryKeys } from '@/shared/api/queryKeys'
import { OperationResultAlert } from '@/shared/ui/OperationResultAlert'
import { useOperationRunner } from '@/shared/ui/useOperationRunner'
import { deleteNas, type NasEntry } from './nasApi'
import { outcomeWarning } from './outcomeWarning'

interface NasDeleteModalProps {
  open: boolean
  nas: NasEntry | null
  onClose: () => void
  /** O'chirish muvaffaqiyatli bo'lsa — ochiq kartochkani yopish uchun */
  onDeleted: () => void
}

/**
 * NAS o'chirish — TASDIQLASH bilan va OQIBAT AYTILGAN holda.
 *
 * ⚠️ NEGA ODDIY `Modal.confirm` YETARLI EMAS. Bu jadvaldagi bitta qator
 * emas: FreeRADIUS faqat `nas` da RO'YXATDAN O'TGAN qurilmadan kelgan
 * so'rovni ko'radi, qolganini JIMGINA tashlab yuboradi. Ya'ni yozuv
 * o'chirilishi bilan o'sha router ortidagi HAMMA abonent internetsiz
 * qoladi — va nosozlik panelda hech qanday xato bo'lib ko'rinmaydi,
 * chunki panel tomonda hech narsa buzilmagan.
 *
 * Shuning uchun ikki qatlam:
 *   1. Oqibat MATN bilan aytiladi ("ishonchingiz komilmi?" emas)
 *   2. Tasdiqlash uchun qurilmaning QISQA NOMI qo'lda yoziladi — bu
 *      "keyingi" tugmasini avtomatik bosishni to'xtatadi va operator
 *      QAYSI qurilmani o'chirayotganini o'z ko'zi bilan tekshiradi
 *      (`actions.typeToConfirm` bilan bir xil andoza)
 */
export function NasDeleteModal({ open, nas, onClose, onDeleted }: NasDeleteModalProps) {
  const { t } = useTranslation()
  const { message } = App.useApp()
  const [typed, setTyped] = useState('')
  const runner = useOperationRunner([queryKeys.nas.all])

  const confirmed = nas !== null && typed.trim() === nas.shortname

  const close = () => {
    runner.clear()
    setTyped('')
    onClose()
  }

  return (
    <Modal
      open={open}
      title={t('nas.deleteTitle')}
      onCancel={close}
      onOk={() => {
        // ⚠️ `nas?.id ?? 0` EMAS: noma'lum NAS uchun O'CHIRISH so'rovini
        // "nol" identifikator bilan yuborish mumkin emas. Tugma
        // `confirmed` bilan bloklangan bo'lsa ham, himoya faqat
        // shunga tayanmasligi kerak — qorovul shu yerda ham turadi.
        if (!nas) return
        void runner.run(() => deleteNas(nas.id)).then((outcome) => {
          if (outcome.status !== 'FAILED') {
            if (outcome.status === 'OK') void message.success(t('common.toastDeleted'))
            else void message.warning(outcomeWarning(outcome, t))
            setTyped('')
            onDeleted()
          }
        })
      }}
      okText={t('nas.deleteSubmit')}
      okButtonProps={{ danger: true, disabled: !confirmed }}
      cancelText={t('common.cancel')}
      confirmLoading={runner.running}
      destroyOnHidden
      width={520}
    >
      <Alert
        type="error"
        showIcon
        style={{ marginBottom: 16 }}
        message={t('nas.deleteWarnTitle')}
        description={t('nas.deleteWarnText')}
      />

      <Typography.Paragraph>
        {t('nas.deleteSubject', { shortname: nas?.shortname ?? '', nasname: nas?.nasname ?? '' })}
      </Typography.Paragraph>

      {runner.result ? (
        <div style={{ marginBottom: 16 }}>
          <OperationResultAlert partialNote={false} result={runner.result} />
        </div>
      ) : null}

      <Typography.Text type="secondary">
        {t('actions.typeToConfirm', { value: nas?.shortname ?? '' })}
      </Typography.Text>
      <Input
        value={typed}
        onChange={(e) => setTyped(e.target.value)}
        placeholder={nas?.shortname ?? ''}
        disabled={runner.running}
        style={{ marginTop: 8 }}
      />
    </Modal>
  )
}
